import {
    AssessmentGroupModel,
    AssessmentGroupUpdateModel,
    AssessmentGroupWriteModel,
    AssessmentModel,
    AssessmentStructureTreeModel,
    AssessmentWriteModel,
    IAssessmentGroupUpdateModel,
    IAssessmentGroupWriteModel,
    IAssessmentWriteModel,
    ManualMarkBatchModel,
    ManualMarkBatchResultModel,
    ManualMarkEntryModel,
    MarkEntrySheetModel,
    MarkEntrySheetRequest,
    MarkExplanationModel,
    MarkWorkflowRequest,
    MarkWorkflowResultModel,
    MoveAssessmentGroupModel,
} from "../api/APIClient";
import { activitiesClient, manualMarksClient, markEntrySheetClient, markWorkflowClient } from "../api/clients";
import { toScopeModels, CalculationScope } from "../calculations/scopes";

/**
 * The assessment tree inside a qualification module, and the marks captured against it.
 *
 * `AssessmentGroup` is the structural tree the calculation engine targets - "Term 1" containing
 * "Division Tests" containing assessments. Every node carries an `AssessmentGroupType`, and procedure
 * selectors match on that *type* rather than on the node, which is what lets one procedure apply
 * across many modules. Do not confuse it with `TeachingGroup`, which is a class roster.
 */

export type { AssessmentGroupModel, AssessmentModel, AssessmentStructureTreeModel, MarkEntrySheetModel, MarkExplanationModel };

// ------------------------------------------------------------- structure tree

export const getModuleStructure = (qualificationModuleID: string): Promise<AssessmentStructureTreeModel> =>
    activitiesClient.getModuleStructure(qualificationModuleID);

/**
 * When a parent group is chosen the owning module is deliberately NOT sent - the API derives it from
 * the parent, and sending both risks claiming a group belongs to a module its parent does not.
 */
export const createGroup = (model: IAssessmentGroupWriteModel): Promise<AssessmentGroupModel> =>
    activitiesClient.createGroup(
        new AssessmentGroupWriteModel(
            model.parentGroupID ? { ...model, qualificationModuleID: undefined } : model
        )
    );

export const updateGroup = (id: string, model: IAssessmentGroupUpdateModel): Promise<AssessmentGroupModel> =>
    activitiesClient.updateGroup(id, new AssessmentGroupUpdateModel(model));

/** Pass an undefined parent to move a group to the top level of its module. */
export const moveGroup = (id: string, parentGroupID?: string): Promise<AssessmentGroupModel> =>
    activitiesClient.moveGroup(id, new MoveAssessmentGroupModel({ parentGroupID }));

export const deleteGroup = (id: string): Promise<boolean> => activitiesClient.deleteGroup(id);

export const createAssessment = (model: IAssessmentWriteModel): Promise<AssessmentModel> =>
    activitiesClient.createAssessment(new AssessmentWriteModel(model));

export const updateAssessment = (id: string, model: IAssessmentWriteModel): Promise<AssessmentModel> =>
    activitiesClient.updateAssessment(id, new AssessmentWriteModel(model));

export const deleteAssessment = (id: string): Promise<boolean> => activitiesClient.deleteAssessment(id);

// ----------------------------------------------------------------- mark entry

export interface MarkSheetQuery {
    qualificationModuleID: string;
    targetEntityType: string;
    targetEntityID: string;
    markTypeID: string;
    /** Narrows the roster to one class group. Optional, and worth using - the sheet is module-wide without it. */
    teachingGroupID?: string;
    includeInactive?: boolean;
}

export const getMarkSheet = (query: MarkSheetQuery): Promise<MarkEntrySheetModel> =>
    markEntrySheetClient.sheet(new MarkEntrySheetRequest(query));

/**
 * The stored provenance for a calculated mark, read back from `MarkResult.Explain`. It is never
 * recomputed - that is the "calculate once, store forever" invariant. If this disagrees with what the
 * engine would produce today, the difference is the point, not a bug to paper over.
 */
export const getMarkExplanation = (markResultID: string): Promise<MarkExplanationModel> =>
    markEntrySheetClient.explanation(markResultID);

export interface ManualMarkEntry {
    studentID: string;
    result: number;
    /**
     * Required when the target is a group or module; must be omitted when the target is an
     * Assessment, because the API derives it from `Assessment.Total`.
     */
    total?: number;
}

export const enterManualMarks = (input: {
    targetEntityType: string;
    targetEntityID: string;
    markTypeID: string;
    teachingGroupID?: string;
    entries: ManualMarkEntry[];
}): Promise<ManualMarkBatchResultModel> =>
    manualMarksClient.enter(
        new ManualMarkBatchModel({
            targetEntityType: input.targetEntityType,
            targetEntityID: input.targetEntityID,
            markTypeID: input.markTypeID,
            teachingGroupID: input.teachingGroupID,
            entries: input.entries.map((e) => new ManualMarkEntryModel(e)),
        })
    );

// ------------------------------------------------------------- mark workflow

/**
 * Finalization and moderation are independent workflow axes, and each action needs its own
 * permission server-side: Finalize/Unfinalize need marks.finalize, Submit needs marks.write, and
 * Approve/Reject need marks.moderate. A Reject with a blank comment is refused.
 */
export type MarkWorkflowAction = "Submit" | "Approve" | "Reject" | "Finalize" | "Unfinalize";

export const applyMarkWorkflow = (action: MarkWorkflowAction, scopes: CalculationScope[], comment?: string): Promise<MarkWorkflowResultModel> =>
    markWorkflowClient.apply(new MarkWorkflowRequest({ action, scopes: toScopeModels(scopes), comment }));
